import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  ArrowRight,
  Dna,
  Beaker,
  Atom,
  Heart,
  Layers
} from 'lucide-react';

const FlashcardsSelect = () => {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();

  const hasActiveSubscription = subscription?.hasSubscription;

  const handleCategoryClick = (category) => {
    if (!user) {
      navigate('/register');
      return;
    }
    if (!hasActiveSubscription) {
      navigate('/pricing');
      return;
    }
    navigate(`/flashcards/${category}`);
  };

  const categories = [
    {
      name: 'Biologie',
      icon: Dna,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500/10',
      questions: 200,
      description: 'Buňky, genetika, ekologie a více'
    },
    {
      name: 'Chemie',
      icon: Beaker,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-500/10',
      questions: 200,
      description: 'Organická, anorganická, biochemie'
    },
    {
      name: 'Fyzika',
      icon: Atom,
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500/10',
      questions: 200,
      description: 'Mechanika, optika, elektřina'
    },
    {
      name: 'Somatologie',
      icon: Heart,
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-500/10',
      questions: 40,
      description: 'Anatomie a fyziologie člověka'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-12">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 -left-40 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-dark-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Zpět</span>
          </button>
        </div>

        {/* Title */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center mb-6">
            <Layers size={32} className="text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Flashcards
          </h1>
          <p className="text-dark-400 max-w-md mx-auto">
            Vyberte kategorii a procvičujte si látku pomocí interaktivních kartiček s animací otáčení.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categories.map((category, index) => (
            <button
              key={index}
              onClick={() => handleCategoryClick(category.name)}
              className="glass rounded-2xl p-6 text-left transition-all hover:ring-2 hover:ring-primary-500/50 group"
            >
              <div className="flex items-start space-x-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center flex-shrink-0`}>
                  <category.icon size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-1">{category.name}</h3>
                  <p className="text-dark-400 text-sm mb-3">{category.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-dark-500 text-sm">{category.questions} kartiček</span>
                    <span className="flex items-center text-primary-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                      Začít <ArrowRight size={16} className="ml-1" />
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Info */}
        <div className="mt-12 glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Jak používat Flashcards?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-bold">1</span>
              </div>
              <p className="text-dark-400">Klikněte na kartičku pro zobrazení odpovědi</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-bold">2</span>
              </div>
              <p className="text-dark-400">Označte kartičky, které už umíte, jako naučené</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-bold">3</span>
              </div>
              <p className="text-dark-400">Zamíchejte kartičky pro náhodné pořadí</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashcardsSelect;
